import React, { Component, Fragment } from 'react';

import PropTypes from 'prop-types';
import qs from 'query-string';
import { connect } from 'react-redux';

import { Redirect } from 'wanderu-component-react-router-link';
import { LiveCheckModal } from 'wanderu-components-search';
import { partnerSelectors } from 'wanderu-ducks-partner-data';
import {
    liveCheckItinerary,
    searchActions,
    tripSummarySelectors,
} from 'wanderu-ducks-search';
import { tripSelectors } from 'wanderu-ducks-trips';
import { getItineraryId } from 'wanderu-utils';

import {
    IN_PROGRESS,
    MIN_LIVE_CHECK_LOAD_TIME,
    NOT_FOUND,
    NOT_STARTED,
    RETURN_DIRECTION,
    SUCCESS_NO_CHANGE,
    SUCCESS_PRICE_DOWN,
    SUCCESS_UNAVAILABLE,
} from '../../constants';

class LiveCheckModalContainer extends Component {
    state = {
        minimumTimeReached: false,
        modalState: NOT_STARTED,
        originalTripPrices: this.props.currentTripPrices,
        redirect: false,
    };

    minLoadTimerId = undefined;

    componentDidMount() {
        const {
            affiliateId,
            departLiveChecksHaveExpired,
            departLiveChecksSuccess,
            direction,
            itineraryId,
            liveCheckSummaryStatus,
            liveChecksHaveExpired,
            liveChecksInProgress,
            liveChecksSuccess,
            seats,
            trackingId,
            tripIds,
            tripSource,
            wheelchairs,
        } = this.props;

        const invalidDepartLiveChecks = direction === RETURN_DIRECTION
            && (!departLiveChecksSuccess || departLiveChecksHaveExpired);

        const isUnavailable = liveCheckSummaryStatus === SUCCESS_UNAVAILABLE;

        if ((invalidDepartLiveChecks || (liveChecksSuccess && !liveChecksHaveExpired))
            && !isUnavailable) {
            return this.onContinue();
        }

        this.setState(() => ({ modalState: IN_PROGRESS }));

        if (!liveChecksInProgress && !isUnavailable) {
            this.props.liveCheckItinerary(tripIds, {
                affiliateId,
                fareVersion: 'v2',
                itineraryId,
                seats,
                trackingId,
                tripIds,
                tripSource,
                wheelchairs,
            });
        }

        const minLoadTime = isUnavailable
            ? 0
            : MIN_LIVE_CHECK_LOAD_TIME;
        this.minLoadTimerId = window.setTimeout(
            () => {
                this.setState(() => ({ minimumTimeReached: true }));
            },
            minLoadTime,
        );

        return undefined;
    }

    componentDidUpdate(prevProps, prevState = {}) {
        const {
            departTripId,
            direction,
            liveChecksCompleted,
            returnTripId,
        } = this.props;

        const {
            departTripId: prevDepartTripId,
            liveChecksCompleted: prevLiveChecksCompleted,
            returnTripId: prevReturnTripId,
        } = prevProps;

        const {
            minimumTimeReached,
            redirect,
        } = this.state;

        const {
            minimumTimeReached: prevMinimumTimeReached,
        } = prevState;

        if (redirect) {
            this.setState(() => ({ redirect: false }));
        }

        if (
            (!prevLiveChecksCompleted && liveChecksCompleted)
            || (!prevMinimumTimeReached && minimumTimeReached)
        ) {
            this.setModalState();
        }

        const shouldRedirect = direction === RETURN_DIRECTION
            ? !!returnTripId && !prevReturnTripId
            : !!departTripId && !prevDepartTripId;

        if (shouldRedirect) {
            this.setState(() => ({ redirect: true }));
        }
    }

    componentWillUnmount() {
        if (this.minLoadTimerId) {
            window.clearTimeout(this.minLoadTimerId);
        }
    }

    onContinue = () => {
        const {
            direction,
            hasReturn,
            onClose,
            setDirection,
            tripIdToLiveCheck,
        } = this.props;

        if (direction === RETURN_DIRECTION) {
            this.props.setReturnTripId(tripIdToLiveCheck);
        } else {
            this.props.setDepartTripId(tripIdToLiveCheck);
        }

        if (direction !== RETURN_DIRECTION && hasReturn) {
            window.scrollTo(0, 0);
            onClose();
            return setDirection(RETURN_DIRECTION);
        }

        return undefined;
    };

    onGoBack = () => {
        const {
            onClose,
            tripIdToLiveCheck,
        } = this.props;

        const { modalState } = this.state;

        let tripIdToRemove;
        if ([NOT_FOUND, SUCCESS_UNAVAILABLE].includes(modalState)) {
            tripIdToRemove = tripIdToLiveCheck;
        }

        onClose(tripIdToRemove);
    };

    buildTripSummaryUrl() {
        const {
            departDate,
            departTripId,
            direction,
            returnDate,
            tripIdToLiveCheck,
        } = this.props;

        const queryString = `?${qs.stringify({
            departDate,
            returnDate,
        })}`;

        if (direction !== RETURN_DIRECTION) {
            return `/tripsummary/${departTripId}/${queryString}`;
        }

        return `/tripsummary/${departTripId}/${tripIdToLiveCheck}/${queryString}`;
    }

    setModalState = () => {
        const modalState = this.getModalState();

        if (modalState === SUCCESS_NO_CHANGE || modalState === SUCCESS_PRICE_DOWN) {
            return this.onContinue();
        }

        if (modalState === this.state.modalState) {
            return undefined;
        }

        this.setState(() => ({ modalState }));

        return undefined;
    };

    getModalState = () => {
        const {
            liveChecksInProgress,
            liveCheckSummaryStatus,
        } = this.props;

        const {
            minimumTimeReached,
        } = this.state;

        if (liveChecksInProgress || !minimumTimeReached) {
            return IN_PROGRESS;
        }

        return liveCheckSummaryStatus;
    };

    render() {
        const {
            carrierCode,
            carrierName,
            departDateTimeUTC,
            destinationPlaceId,
            funnelStage,
            isRouted,
            itineraryId,
            liveCheckData,
            originPlaceId,
            reduxAllFilterParams,
            seats,
            tripIdToLiveCheck,
            tripPrices,
            trips,
        } = this.props;
        const {
            modalState,
            originalTripPrices,
            redirect,
        } = this.state;

        return (
            <Fragment>
                <LiveCheckModal
                    carrierCode={carrierCode}
                    carrierName={carrierName}
                    departDateTimeUTC={departDateTimeUTC}
                    funnelStage={funnelStage}
                    isRouted={isRouted}
                    itineraryId={itineraryId}
                    liveCheckData={liveCheckData}
                    modalState={modalState}
                    newPrice={tripPrices}
                    onContinue={this.onContinue}
                    onGoBack={this.onGoBack}
                    originalPrice={originalTripPrices}
                    seats={seats}
                    tripId={tripIdToLiveCheck}
                    trips={trips}
                />
                {redirect && (
                    <Redirect
                        href={this.buildTripSummaryUrl()}
                        queries={{
                            dpid: destinationPlaceId,
                            dtid: undefined,
                            opid: originPlaceId,
                            p: seats > 1
                                ? seats
                                : undefined,
                            ...reduxAllFilterParams,
                        }}
                        push
                    />
                )}
            </Fragment>
        );
    }
}

LiveCheckModalContainer.propTypes = {
    affiliateId: PropTypes.string,
    carrierCode: PropTypes.string,
    carrierName: PropTypes.string,
    currentTripPrices: PropTypes.array,
    departDate: PropTypes.string,
    departDateTimeUTC: PropTypes.number,
    departLiveChecksHaveExpired: PropTypes.bool,
    departLiveChecksSuccess: PropTypes.bool,
    departTripId: PropTypes.string,
    destinationPlaceId: PropTypes.string,
    direction: PropTypes.string,
    funnelStage: PropTypes.string,
    hasReturn: PropTypes.bool,
    isRouted: PropTypes.bool,
    itineraryId: PropTypes.string,
    liveCheckData: PropTypes.object,
    liveCheckItinerary: PropTypes.func,
    liveChecksCompleted: PropTypes.bool,
    liveChecksHaveExpired: PropTypes.bool,
    liveChecksInProgress: PropTypes.bool,
    liveChecksSuccess: PropTypes.bool,
    liveCheckSummaryStatus: PropTypes.string,
    onClose: PropTypes.func,
    originPlaceId: PropTypes.string,
    reduxAllFilterParams: PropTypes.object,
    returnDate: PropTypes.string,
    returnTripId: PropTypes.string,
    seats: PropTypes.number,
    setDepartTripId: PropTypes.func,
    setDirection: PropTypes.func,
    setReturnTripId: PropTypes.func,
    trackingId: PropTypes.string,
    tripIds: PropTypes.arrayOf(PropTypes.string),
    tripIdToLiveCheck: PropTypes.string,
    tripPrices: PropTypes.array,
    trips: PropTypes.array,
    tripSource: PropTypes.string,
    wheelchairs: PropTypes.number,
};

const mapDispatchToProps = {
    liveCheckItinerary: liveCheckItinerary,
    setDepartTripId: searchActions.setDepartTripId,
    setDirection: searchActions.setDirection,
    setReturnTripId: searchActions.setReturnTripId,
};

const mapStateToProps = (state, ownProps) => {
    const {
        departTripId,
        tripIdToLiveCheck,
    } = ownProps;

    const { seats = 1, wheelchairs = 0 } = tripSummarySelectors.selectTravelerInfo(state) || {};
    const lcOpts = { seats, wheelchairs };

    const trip = tripSelectors.selectTrip(state, tripIdToLiveCheck);
    const tripSet = tripSelectors.selectTripSet(state, tripIdToLiveCheck);
    const tripIds = tripSet.map(({ tripId }) => tripId);

    const tripPrices = tripSummarySelectors.selectPrices(
        state,
        [tripIdToLiveCheck],
        seats,
        wheelchairs,
    );
    const { pricing: currentTripPrices } = tripSelectors.selectTripPrices(
        state,
        tripIdToLiveCheck,
        seats,
        wheelchairs,
    );
    const tripCarrierData = tripSelectors.selectTripCarrierData(state, tripIdToLiveCheck);
    const partnerDetails = partnerSelectors.selectPartnerDetails(state);
    const liveChecksInProgress = tripSummarySelectors.selectLiveChecksInProgress(
        state,
        tripIds,
        lcOpts,
    );
    const liveChecksSuccess = tripSummarySelectors.selectLiveChecksSuccess(
        state,
        tripIds,
        lcOpts,
    );
    const liveChecksHaveExpired = tripSummarySelectors.selectLiveChecksHaveExpired(
        state,
        tripIds,
        lcOpts,
    );
    const liveCheckSummaryStatus = tripSummarySelectors.selectLiveCheckSummaryStatus(
        state,
        tripIds,
        lcOpts,
    );
    const liveChecksCompleted = tripSummarySelectors.selectLiveChecksCompleted(
        state,
        tripIds,
        lcOpts,
    );
    const liveCheckData = tripSummarySelectors.selectLiveCheckData(
        state,
        tripIds,
        lcOpts,
    );

    const departTrips = tripSelectors.selectTripSet(state, departTripId);
    const departTripIds = departTrips.map((departTrip) => departTrip.tripId);
    const departLiveChecksSuccess = tripSummarySelectors.selectLiveChecksSuccess(
        state,
        departTripIds,
        lcOpts,
    );
    const departLiveChecksHaveExpired = tripSummarySelectors.selectLiveChecksHaveExpired(
        state,
        departTripIds,
        lcOpts,
    );

    let itineraryId = getItineraryId(tripIds);

    if (departTripIds.length) {
        // if departTripIds has a length, we're LCing a return trip
        itineraryId = getItineraryId(departTripIds, tripIds);
    }

    return {
        affiliateId: partnerDetails.affiliateId,
        carrierCode: tripCarrierData.carrierCode,
        carrierName: tripCarrierData.name,
        currentTripPrices,
        departDateTimeUTC: trip.departDateTimeUTC,
        departLiveChecksHaveExpired,
        departLiveChecksSuccess,
        isRouted: trip.isRouted,
        itineraryId,
        liveCheckData,
        liveChecksCompleted,
        liveChecksHaveExpired,
        liveChecksInProgress,
        liveChecksSuccess,
        liveCheckSummaryStatus,
        seats,
        trackingId: partnerDetails.trackingId,
        tripIds,
        tripPrices,
        trips: tripSet,
        tripSource: trip.tripSource,
        wheelchairs,
    };
};

export {
    LiveCheckModalContainer,
    mapStateToProps,
};
export default connect(mapStateToProps, mapDispatchToProps)(LiveCheckModalContainer);
